import React, { useLayoutEffect, useRef, useState } from 'react';
import './ButtonsStack.css';
import clsx from 'clsx';
import ElementBlank from '@/assets/images/elements-png/element-blank.png';
import ElementButton from '@/assets/images/elements-png/element-button.png';
import ElementAll from '@/assets/images/elements-png/element-all.png';
import ElementImage from '@/assets/images/elements-png/element-image.png';
import ElementText from '@/assets/images/elements-png/element-text.png';
import Element2Columns from '@/assets/images/elements-png/add-2-columns.png';
import Element2Rows from '@/assets/images/elements-png/add-2-rows.png';
import ImageHeroSection from '@/assets/images/elements-png/element-hero.png';
import ImageFeaturesOverviewSection from '@/assets/images/elements-png/element-feature.png';
import ImageLayerAdd from '@/assets/images/elements-png/element-more.png';

import ImageHeroSectionHover from '@/assets/images/elements-png/element-hero-hover.png';
import ImageFeaturesOverviewSectionHover from '@/assets/images/elements-png/element-feature-hover.png';

import ElementBlankHover from '@/assets/images/elements-png/element-blank-hover.png';
import ElementButtonHover from '@/assets/images/elements-png/element-button-hover.png';
import ElementAllHover from '@/assets/images/elements-png/element-all-hover.png';
import ElementImageHover from '@/assets/images/elements-png/element-image-hover.png';
import ElementTextHover from '@/assets/images/elements-png/element-text-hover.png';
import Element2ColumnsHover from '@/assets/images/elements-png/add-2-columns-hover.png';
import Element2RowsHover from '@/assets/images/elements-png/add-2-rows-hover.png';
import { ElementCard } from '@/components/panels/elements/ElementCard/ElementCard';
import {
  BUTTON_NODE_NAME,
  CONTENT_BLOCK_NAME,
  IMAGE_NODE_NAME,
  TEXT_NODE_NAME,
} from '@/utils/constants';
import { useAppSelector } from '@/store/hooks';
import { selectScale } from '@/store/zoom/selectors';
import { selectIsFeatureEnabled } from '@/store/featureFlags/selectors';

interface ButtonStackProps {
  className?: string;
  onSelect?: (elementName: string) => void;
  styles?: React.CSSProperties;
  flexDirection?: 'row' | 'column';
  isOnlyRootContainer?: boolean;
}

export const ButtonStack: React.FC<ButtonStackProps> = ({
  className = '',
  onSelect,
  styles,
  flexDirection = 'row',
  isOnlyRootContainer = false,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const zoom = useAppSelector(selectScale);
  const isInspirationPagesEnabled = useAppSelector(
    selectIsFeatureEnabled('builder4-inspiration-pages'),
  );
  let zoomFactor = 1;
  if (zoom > 1) {
    zoomFactor = 1 / zoom;
  }

  const [scale, setScale] = useState(1);
  // Consistent inner padding around scaled content (px)
  const PADDING = 8;
  // Disable scale transition while user is interacting (hover/click)
  const [isInteracting, setIsInteracting] = useState(false);
  // Track initial measure to avoid first-frame animation/pop
  const [isMeasured, setIsMeasured] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    let raf = 0;

    const compute = () => {
      // Use scrollWidth/scrollHeight to get intrinsic size (ignores CSS transforms)
      const contentW = (content.scrollWidth || 1) * zoomFactor;
      const contentH = (content.scrollHeight || 1) * zoomFactor;
      // Subtract inner padding to keep a safe margin when scaling
      const containerW = Math.max(1, (container.clientWidth || 1) - PADDING * 2);
      const containerH = Math.max(1, (container.clientHeight || 1) - PADDING * 2);
      const next = Math.min(containerW / contentW, containerH / contentH, 1);
      // Avoid tiny re-renders due to float noise
      if (!Number.isFinite(next)) return;
      setScale((prev) => (Math.abs(prev - next) > 0.002 ? next : prev));
    };

    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };

    // Observe container size changes
    const roContainer = new ResizeObserver(schedule);
    roContainer.observe(container);

    // We intentionally do NOT observe content to avoid re-scaling
    // during hover-driven micro layout changes which cause animation.

    // Initial measure synchronously to avoid first-frame pop/animation
    compute();
    setIsMeasured(true);
    // Schedule future changes on next frames
    schedule();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      roContainer.disconnect();
    };
  }, []);

  const handleClick = (elementName: string) => {
    onSelect?.(elementName);
  };

  return (
    <div
      ref={containerRef}
      style={{
        ...(styles || {}),
        // Reserve an inner gap so the scaled content never hugs the edges
        padding: PADDING,
        pointerEvents: 'auto',
      }}
      className={clsx('button-stack-wrapper', className)}
      onPointerEnter={() => setIsInteracting(true)}
      onPointerDown={() => setIsInteracting(true)}
      onPointerUp={() => setIsInteracting(false)}
      onPointerLeave={() => setIsInteracting(false)}
    >
      <div
        ref={contentRef}
        style={{
          transform: `scale(${scale * zoomFactor})`,
          transformOrigin: 'center center',
          transition: isInteracting || !isMeasured ? 'none' : 'transform 200ms ease',
          willChange: 'transform',
          width: 'fit-content',
          height: 'fit-content',
          pointerEvents: 'auto',
        }}
      >
        <div className="button-stack-layout">
          {isOnlyRootContainer && (
            <>
              <p className="drag-drop-hint font-inter mt-4 text-center text-[12px] leading-[20px] font-semibold text-[#575F6A]">
                Start with a pre-made section
              </p>
              <div className="button-stack-group">
                <ElementCard
                  className="hover:bg-[#d9d4fa]"
                  classNameImg="group-hover:shadow-[0px_2px_6px_0px_rgba(98,69,242,0.30)] rounded-md"
                  imgSrc={ImageHeroSection}
                  imageHover={ImageHeroSectionHover}
                  onClick={() => {
                    handleClick('HERO_SECTION');
                  }}
                  state="default"
                  text="Hero Section"
                  type="canvas"
                  size="default"
                  data-qa-selector="canvas-add-hero-section"
                />

                {isInspirationPagesEnabled && (
                  <ElementCard
                    className="hover:bg-[#d9d4fa]"
                    classNameImg="group-hover:shadow-[0px_2px_6px_0px_rgba(98,69,242,0.30)] rounded-md"
                    imgSrc={ImageFeaturesOverviewSection}
                    imageHover={ImageFeaturesOverviewSectionHover}
                    onClick={() => {
                      handleClick('INSPIRATION');
                    }}
                    state="default"
                    text="Inspiration"
                    type="canvas"
                    size="default"
                    data-qa-selector="canvas-add-feature-section"
                  />
                )}

                <ElementCard
                  className="hover:bg-[#d9d4fa]"
                  classNameImg="group-hover:shadow-[0px_2px_6px_0px_rgba(98,69,242,0.30)] rounded-md"
                  imgSrc={ImageLayerAdd}
                  imageHover={ImageLayerAdd}
                  onClick={() => {
                    handleClick('EXPLORE_MORE');
                  }}
                  state="default"
                  text="Explore more"
                  type="canvas"
                  size="default"
                  data-qa-selector="canvas-explore-more"
                />
              </div>
              <p className="drag-drop-hint font-inter mt-4 text-center text-[12px] leading-[20px] font-semibold text-[#575F6A]">
                Start from scratch
              </p>
            </>
          )}
          <div className="button-stack-group">
            <ElementCard
              className="hover:bg-[#d9d4fa]"
              classNameImg="group-hover:shadow-[0px_2px_6px_0px_rgba(98,69,242,0.30)] rounded-md"
              imgSrc={ElementBlank}
              imageHover={ElementBlankHover}
              onClick={() => {
                handleClick(CONTENT_BLOCK_NAME);
              }}
              state="default"
              text="Container"
              type="canvas"
              size="default"
              data-qa-selector="canvas-add-container"
            />
            <ElementCard
              className="hover:bg-[#FEE3E3]"
              classNameImg="group-hover:shadow-[0px_2px_6px_0px_rgba(220,53,38,0.40)] rounded-md"
              imgSrc={flexDirection === 'column' ? Element2Rows : Element2Columns}
              imageHover={flexDirection === 'column' ? Element2RowsHover : Element2ColumnsHover}
              onClick={() => {
                handleClick(flexDirection === 'column' ? 'ROW_CONTAINERS' : 'COLUMN_CONTAINERS');
              }}
              state="default"
              text={flexDirection === 'column' ? '2 Rows' : '2 Columns'}
              type="canvas"
              size="default"
              data-qa-selector={
                flexDirection === 'column' ? 'canvas-add-2-rows' : 'canvas-add-2-columns'
              }
            />
            <ElementCard
              className="hover:bg-[#CFF1E6]"
              classNameImg="group-hover:shadow-[0px_2px_6px_0px_rgba(16,185,109,0.40)] rounded-md"
              imgSrc={ElementImage}
              imageHover={ElementImageHover}
              onClick={() => {
                handleClick(IMAGE_NODE_NAME);
              }}
              state="default"
              text="Image"
              type="canvas"
              size="default"
              data-qa-selector="canvas-add-image"
            />
          </div>

          <div className="button-stack-group">
            <ElementCard
              className="hover:bg-[#D0F1ED]"
              classNameImg="group-hover:shadow-[0px_2px_6px_0px_rgba(25,176,175,0.40)] rounded-md"
              imgSrc={ElementText}
              imageHover={ElementTextHover}
              onClick={() => {
                handleClick(TEXT_NODE_NAME);
              }}
              state="default"
              text="Text"
              type="canvas"
              size="default"
              data-qa-selector="canvas-add-text"
            />

            <ElementCard
              className="hover:bg-[#FEE3D0]"
              classNameImg="group-hover:shadow-[0px_2px_6px_0px_rgba(251,129,36,0.40)] rounded-md"
              imgSrc={ElementButton}
              imageHover={ElementButtonHover}
              onClick={() => {
                handleClick(BUTTON_NODE_NAME);
              }}
              state="default"
              text="Button"
              type="canvas"
              size="default"
              data-qa-selector="canvas-add-button"
            />

            <ElementCard
              className="hover:bg-[#DBDDE0]"
              classNameImg="group-hover:shadow-[0px_2px_6px_0px_rgba(174,179,185,0.40)] rounded-md"
              imgSrc={ElementAll}
              imageHover={ElementAllHover}
              onClick={() => {
                handleClick('All');
              }}
              state="default"
              text="All Elements"
              type="canvas"
              size="default"
              data-qa-selector="canvas-all-elements"
            />
          </div>
        </div>

        <p className="drag-drop-hint font-inter mt-4 text-center text-[12px] leading-[20px] font-semibold text-[#575F6A]">
          Click or drag in elements
        </p>
      </div>
    </div>
  );
};
